import React, { useState, useEffect } from "react";
import { useOwnerStore } from "../store/OwnerStore";
import Swal from "sweetalert2";
import Loader from "./Loader";
import { useForm } from "react-hook-form";
import { debounce } from 'lodash';
import { useFormStep } from "../store/FormStepStore";
import { useAuth } from "../store/Auth";
import {
  base,
  states as stateUrl,
  lgs as lgsUrl,
  validate_user,
  get_new_owner_details
} from "../constants/urls";
import validator from "validator";
import InstructionModal from "./InstructionModal";
import { useModal } from "../store/ModalState";
import Header from "./assist/Title";
import { checkToken } from "./CheckToken";

export default function OwnerInfo() {
  const { ownerDetails, setMultipleOwnerDetails, reset, setSingleOwnerDetails } = useOwnerStore();
  const [isLoading, setIsLoading] = useState(false);
  const setStep = useFormStep((state) => state.setStep);
  const getStep = useFormStep((state) => state.step);
  const [lgs, setLocalGovernments] = useState([]);
  const setAuth = useAuth((state) => state.setAuth);
  const getAuth = useAuth((state) => state.auth);
  const [exists, setExists] = useState(false);
  const [warning, setWarning] = useState("");
  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    defaultValues: {
      id_number: ownerDetails.idNumber || "",
      id_type: ownerDetails.idType || "",
      first_name: ownerDetails.firstName || "",
      middle_name: ownerDetails.middleName || "",
      last_name: ownerDetails.lastName || "",
      o_gender: ownerDetails.gender || "",
      phone: ownerDetails.phone || "",
      email: ownerDetails.email || "",
      r_state: ownerDetails.currentState || "",
      lga: ownerDetails.currentLG || "",
      r_address: ownerDetails.address || ""
    }
  });
  const [states, setStates] = useState([]);
  const showModal = useModal((state) => state.showModal);
  const setShowModal = useModal((state) => state.setShowModal);
  const [errorMessage, setErrorMessage] = useState("");
  const [validated, setValidated] = useState(false);

  const handleUpdateField = (field, newValue) => {
    setSingleOwnerDetails(field, newValue);
    setValue(field, newValue);
  };

  const handleUpdateMultiple = (updatedDetails) => {
    setMultipleOwnerDetails(updatedDetails);
    Object.entries(updatedDetails).forEach(([key, value]) => {
      setValue(key, value);
    });
  };

  const handleReset = () => {
    reset();
  };

  // --- Auth & Security ---
  useEffect(() => {
    const verifySession = async () => {
      // WAIT for the token to exist before checking it
      if (getAuth?.authenticated && getAuth?.token) {
        const isValid = await checkToken(getAuth.token);
        if (!isValid) {
          localStorage.clear();
          window.location.replace("/login");
        }
      } else if (getAuth?.authenticated === false) {
        // Only boot them immediately if we are 100% sure they are logged out
        window.location.replace("/login");
      }
    };

    verifySession();
  }, [getAuth?.authenticated, getAuth?.token]);

  useEffect(() => {
    setShowModal(true);
  }, []);

  const validateIdNumber = debounce((value) => {
    if (!value) {
      setErrorMessage("ID Number is required");
      setValidated(false);
      return false;
    }

    if (ownerDetails.profileType === "individual") {
      if (!/^\d{11}$/.test(value)) {
        setErrorMessage("NIN must be exactly 11 digits with no spaces or letters");
        setValidated(false);
        return false;
      }
    } else if (ownerDetails.profileType === "corporate") {
      if (ownerDetails.idType === "cac") {
        if (!/^(RC|BN|IT)\d+$/.test(value)) {
          setErrorMessage("CAC must start with RC, BN, or IT followed by numbers, no spaces");
          setValidated(false);
          return false;
        }
      } else if (ownerDetails.idType === "tin") {
        if (!/^\d{10}$|^\d+(-0001)$/.test(value)) {
          setErrorMessage("TIN must be 10 digits or end with -0001, no spaces or letters");
          setValidated(false);
          return false;
        }
      }
    }

    setErrorMessage("");
    setValidated(true);
    return true;
  }, 300);

  const isTitle = (value) => {
    if (!value) return true;
    const commonTitleRegex = /^(mr|Mr|MR|mrs|Mrs|MRS|mr.|Mr.|MR.|mrs.|Mrs.|MRS.|dr|Dr|DR|dr.|Dr.|DR.|miss|Miss|MISS|miss.|Miss.|MISS.|lady|Lady|LADY|lord|Lord|LORD|Mx|MX|engr|Engr|ENGR|engr.|Engr.|ENGR.)$/i;
    const igboTitles = /^(Obi|Eze|Igwe|Ozo|Lolo|Chief)$/i;
    const yorubaTitles = /^(Oba|Alaafin|Ooni|Iudge|Prof)$/i;
    const hausaTitles = /^(Sarkin|Galadima|Magaji)$/i;
    const religiousTitles = /^(Imam|Alhaji|Mallam|Pastor|Reverend|Bishop|Deacon)$/i;

    const titleRegex = new RegExp([
      commonTitleRegex.source,
      igboTitles.source,
      yorubaTitles.source,
      hausaTitles.source,
      religiousTitles.source,
    ].join('|'));

    return !titleRegex.test(value) || "Titles such as Mr, Mrs, Dr, etc. are not allowed";
  };

  const Proceed = (e) => {
    e.preventDefault();
    if (exists) {
      window.location = "/services/vehicleinformation";
    }
  };

  useEffect(() => {
    if (ownerDetails.profileType === "individual") {
      setWarning("Please Note: Vehicle owners name should be entered as contained in the NIN details i.e. Firstname, Middlename, Lastname. Mismatch of NIN details or using the NIN of another person will result in ID Verification failure. Titles such as Mr, Mrs, Dr, Chief, Rev, Prof, Engr, etc are not allowed.");
    } else {
      setWarning("Organization name should be the same as contained in TIN/CAC details");
    }
  }, [ownerDetails.profileType]);

  const GetOwner = async () => {
    if (!ownerDetails.idType || !ownerDetails.idNumber) return;
    setIsLoading(true);
    let url = base + get_new_owner_details + "?use=" + ownerDetails.idType + "&id=" + ownerDetails.idNumber;
    let token = getAuth.token;
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const resJson = await res.json();
      if (resJson.statusCode === 0) {
        const updatedDetails = {
          firstName: resJson.data.first_name,
          middleName: resJson.data.middle_name,
          lastName: resJson.data.last_name,
          email: resJson.data.email,
          phone: resJson.data.phone,
          address: resJson.data.present_address,
          state: resJson.data.state,
          lga: resJson.data.lga,
        };
        handleUpdateMultiple(updatedDetails);
        setExists(true);
      } else {
        setExists(false);
      }
    } catch (error) {
      // console.error("Error fetching owner details:", error);
      setExists(false);
      // Swal.fire({
      //   title: "Error",
      //   text: "Failed to fetch owner details. Please try again.",
      //   icon: "error",
      //   confirmButtonText: "Ok",
      //   confirmButtonColor: "#1C89DE",
      // });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleReset();
  }, []);

  useEffect(() => {
    let url = base + stateUrl;
    if (!validator.isURL(url)) return;
    fetch(url)
      .then((res) => res.json())
      .then((resJson) => {
        setStates(resJson.data);
      });
  }, []);

  useEffect(() => {
    getLgs();
  }, [ownerDetails.currentState]);

  const SubmitOwner = async () => {
    if (!ownerDetails.idNumber) {
      Swal.fire({
        title: "ID Number is missing",
        text: "",
        icon: "info",
        confirmButtonText: "Ok",
        confirmButtonColor: "#1C89DE",
      });
      return;
    }
    setIsLoading(true);
    if (exists) {
      window.location = "/services/vehicleinformation";
      return;
    }
    try {
      const validationResult = await validateUserInput(ownerDetails.phone, "phone");
      if (validationResult.statusCode === 0) {
        window.location = "/services/vehicleinformation";
      } else {
        Swal.fire({
          title: "Error!",
          text: validationResult.message,
          icon: "error",
          confirmButtonText: "Ok",
          confirmButtonColor: "#1C89DE",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#1C89DE",
      });
    } finally {
      setIsLoading(false);
    }
  };

  async function validateUserInput(input, vwith) {
    if (!input && vwith === "phone") {
      return Promise.reject(new Error("Missing input for validation"));
    }

    let url = base + validate_user + "?validating_input=" + input + "&with=" + vwith;
    let token = getAuth.token;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      }
    });
    const resJson = await res.json();
    return resJson;
  }

  const getLgs = async () => {
    if (!ownerDetails.currentState) {
      setLocalGovernments([]);
      return;
    }
    const url = base + lgsUrl + ownerDetails.currentState;
    fetch(url)
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson.statusCode === 0) {
          setLocalGovernments(resJson.data);
        }
      });
  };

  if (getStep < 3) {
    window.location = "/login";
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 transition-all duration-300 ease-in-out">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-fade-in-down">
          Vehicle Owner Information
        </h2>
        <p className="text-red-600 font-medium mb-8 text-sm sm:text-base animate-fade-in">{warning}</p>
        <form onSubmit={handleSubmit(SubmitOwner)} className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Owner Profile Type <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6 p-3 border-2 border-gray-200 rounded-lg bg-gray-50 transition-all duration-200 hover:border-blue-300">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="owner_profile_type"
                    value="individual"
                    onChange={(e) => handleUpdateField("profileType", e.target.value)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                    required
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Personal</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="owner_profile_type"
                    value="corporate"
                    onChange={(e) => handleUpdateField("profileType", e.target.value)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                    required
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Organization</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                ID Type <span className="text-red-500">*</span>
              </label>
              <select
                name="id_type"
                {...register("id_type", { required: "ID Type is required" })}
                value={ownerDetails.idType}
                onChange={(e) => handleUpdateField("idType", e.target.value)}
                className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
              >
                <option value="">Select ID Type</option>
                {ownerDetails.profileType === "individual" && (
                  <option value="nin">National Identification Number (NIN)</option>
                )}
                {ownerDetails.profileType === "corporate" && (
                  <>
                    <option value="tin">TIN</option>
                    <option value="cac">CAC</option>
                  </>
                )}
              </select>
              {errors.id_type && (
                <p className="text-sm text-red-500 animate-pulse">{errors.id_type.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                ID Number <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="id_number"
                  // {...register("id_number")}
                  value={ownerDetails.idNumber}
                  onChange={(e) => {
                    handleUpdateField("idNumber", e.target.value);
                    validateIdNumber(e.target.value);
                    setExists(false);
                  }}
                  onBlur={() => {
                    // if (validateIdNumber(ownerDetails.idNumber) && ownerDetails.idType) {
                      GetOwner();
                    // }
                  }}
                  placeholder="ID Number"
                  className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                />
                {isLoading && <Loader className="ml-3" />}
              </div>
              {errorMessage && (
                <p className="text-sm text-red-500 animate-pulse">{errorMessage}</p>
              )}
            </div>
          </div>

          {!exists && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {ownerDetails.profileType === "individual" ? "Owner's First Name" : "Name of Organization"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  {...register("first_name", { required: "First Name is required", validate: isTitle })}
                  value={ownerDetails.firstName}
                  onChange={(e) => handleUpdateField("firstName", e.target.value.toUpperCase())}
                  placeholder={ownerDetails.profileType === "individual" ? "Owner's First Name" : "Name of Organization"}
                  className="uppercase w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                />
                {errors.first_name && (
                  <p className="text-sm text-red-500 animate-pulse">{errors.first_name.message || "Check First Name for error"}</p>
                )}
              </div>

              {ownerDetails.profileType === "individual" && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Owner's Middle Name
                    </label>
                    <input
                      type="text"
                      name="middle_name"
                      {...register("middle_name", { validate: isTitle })}
                      value={ownerDetails.middleName}
                      onChange={(e) => handleUpdateField("middleName", e.target.value.toUpperCase())}
                      placeholder="Owner's Middle Name"
                      className="uppercase w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                    />
                    {errors.middle_name && (
                      <p className="text-sm text-red-500 animate-pulse">{errors.middle_name.message || "Check Middle Name for error"}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Owner's Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      {...register("last_name", { required: "Last Name is required", validate: isTitle })}
                      value={ownerDetails.lastName}
                      onChange={(e) => handleUpdateField("lastName", e.target.value.toUpperCase())}
                      placeholder="Owner's Last Name"
                      className="uppercase w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                    />
                    {errors.last_name && (
                      <p className="text-sm text-red-500 animate-pulse">{errors.last_name.message || "Check Last Name for error"}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="o_gender"
                      {...register("o_gender", { required: "Gender is required" })}
                      value={ownerDetails.gender}
                      onChange={(e) => handleUpdateField("gender", e.target.value)}
                      className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    {errors.o_gender && (
                      <p className="text-sm text-red-500 animate-pulse">{errors.o_gender.message}</p>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone No. <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  {...register("phone", { 
                    required: "Phone number is required", 
                    pattern: { value: /^\d{11}$/, message: "Phone number must be 11 digits" },
                    maxLength: { value: 11, message: "Phone number must be 11 digits" },
                    minLength: { value: 11, message: "Phone number must be 11 digits" }
                  })}
                  value={ownerDetails.phone}
                  onChange={(e) => handleUpdateField("phone", e.target.value)}
                  placeholder="080xxxxxxxx"
                  className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                />
                {errors.phone && (
                  <p className="text-sm text-red-500 animate-pulse">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  {...register("email", { 
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email address" }
                  })}
                  value={ownerDetails.email}
                  onChange={(e) => handleUpdateField("email", e.target.value)}
                  placeholder="Email"
                  className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                />
                {errors.email && (
                  <p className="text-sm text-red-500 animate-pulse">{errors.email.message}</p>
                )}
              </div>

              <div className="sm:col-span-2 lg:col-span-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {ownerDetails.profileType === "individual" ? "Current State and LGA of Residence" : "Current State and LGA of Business"} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <select
                      name="r_state"
                      {...register("r_state", { required: "State is required" })}
                      value={ownerDetails.currentState}
                      onChange={(e) => handleUpdateField("currentState", e.target.value)}
                      className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                    >
                      <option value="">Select State</option>
                      {states.map((val, index) => (
                        <option key={index} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                    {errors.r_state && (
                      <p className="text-sm text-red-500 animate-pulse">{errors.r_state.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <select
                      name="lga"
                      {...register("lga", { required: "Local Government is required" })}
                      value={ownerDetails.currentLG}
                      onChange={(e) => handleUpdateField("currentLG", e.target.value)}
                      className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                    >
                      <option value="">Select Local Government</option>
                      {lgs?.map((val, index) => (
                        <option key={index} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                    {errors.lga && (
                      <p className="text-sm text-red-500 animate-pulse">{errors.lga.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 lg:col-span-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {ownerDetails.profileType === "individual" ? "Current Residential Address" : "Current Business Address"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="r_address"
                  {...register("r_address", {
                    required: "Address is required",
                    minLength: { value: 10, message: "Address is too short" },
                  })}
                  value={ownerDetails.address}
                  onChange={(e) => handleUpdateField("address", e.target.value)}
                  placeholder={ownerDetails.profileType === "individual" ? "Current Residential Address" : "Current Business Address"}
                  className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                />
                {errors.r_address && (
                  <p className="text-sm text-red-500 animate-pulse">{errors.r_address.message}</p>
                )}
              </div>
            </div>
          )}

          {exists && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {ownerDetails.profileType === "individual" ? "First Name" : "Name of Organization"}
                </label>
                <input
                  type="text"
                  value={ownerDetails.firstName || ""}
                  readOnly
                  placeholder={ownerDetails.profileType === "individual" ? "First Name" : "Name of Organization"}
                  className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                />
              </div>

              {ownerDetails.profileType === "individual" && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      value={ownerDetails.middleName || ""}
                      readOnly
                      placeholder="Middle Name"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={ownerDetails.lastName || ""}
                      readOnly
                      placeholder="Last Name"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Gender
                    </label>
                    <select
                      name="gender"
                      disabled
                      value={ownerDetails.gender}
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                    >
                      <option value="">{ownerDetails.gender || "Select Gender"}</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone No.
                </label>
                <input
                  type="text"
                  value={ownerDetails.phone || ""}
                  readOnly
                  placeholder="080xxxxxxxx"
                  className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={ownerDetails.email || ""}
                  readOnly
                  placeholder="Email"
                  className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {ownerDetails.profileType === "individual" ? "Current Residential Address" : "Current Business Address"}
                </label>
                <input
                  type="text"
                  value={ownerDetails.address || ""}
                  readOnly
                  placeholder="Current Address"
                  className="w-full rounded-lg border-2 border-gray-200 bg-gray-100 p-3 text-base transition-all duration-200"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between mt-8 space-y-4 sm:space-y-0 sm:space-x-4 animate-fade-in-up">
            <button
              type="button"
              onClick={() => {
                setStep(getStep - 1);
                window.location = "/services";
              }}
              className="w-full sm:w-32 bg-gray-500 text-white rounded-lg py-3 px-4 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type={exists ? "button" : "submit"}
              onClick={exists ? Proceed : undefined}
              disabled={!validated && !exists}
              className={`w-full sm:w-32 bg-blue-500 text-white rounded-lg py-3 px-4 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center ${!validated && !exists ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
            >
              {isLoading ? <Loader /> : "Next >>"}
            </button>
          </div>
        </form>
        <InstructionModal />
      </div>
      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.5s ease-out;
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-pulse {
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}